import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, Sparkle, CircleNotch } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

export default function Rejections() {
  const [items, setItems] = useState([]);
  const [tax, setTax] = useState({ content_types: [], universes: [] });
  const [dlg, setDlg] = useState(null); // {sourceId, oldTitle, universe, content_type, title, loading, generating}

  const load = async () => {
    const { data } = await api.get("/content?status=rejected");
    setItems(data);
  };
  useEffect(() => { api.get("/config/taxonomy").then((r) => setTax(r.data)); }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const typeLabel = (k) => tax.content_types.find((t) => t.key === k)?.label || k;
  const uniLabel = (k) => tax.universes.find((u) => u.key === k)?.label || k;
  const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; } };

  const openSuggest = async (c) => {
    setDlg({ sourceId: c.id, oldTitle: c.title_primary, universe: c.universe, content_type: c.content_type, title: "", loading: true, generating: false });
    try {
      const { data } = await api.post(`/content/${c.id}/relaunch-suggest`);
      setDlg((d) => d && d.sourceId === c.id ? { ...d, title: data.title || "", universe: data.universe, content_type: data.content_type, loading: false } : d);
    } catch (e) {
      toast.error("Impossible de proposer un angle");
      setDlg(null);
    }
  };

  const generate = async () => {
    if (!dlg?.title?.trim()) { toast.error("L'angle ne peut pas être vide"); return; }
    setDlg((d) => ({ ...d, generating: true }));
    try {
      await api.post("/content/generate", { content_type: dlg.content_type, universe: dlg.universe, topic: dlg.title.trim() });
      toast.success("Génération lancée", { description: "L'article amélioré arrivera dans la file de validation d'ici 1-2 min." });
      setDlg(null);
    } catch (e) { toast.error("Génération impossible"); setDlg((d) => d && ({ ...d, generating: false })); }
  };

  return (
    <div data-testid="rejections-page">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-1">Historique</div>
        <h1 className="font-serif text-4xl text-brand tracking-tight">Sujets rejetés</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Les articles que vous avez refusés. Un sujet rejeté n'est plus jamais reproposé par la veille ni régénéré automatiquement — cet historique vous aide à repérer ce qui ne vous convient pas et à affiner la stratégie.</p>
      </div>

      <div className="bg-white border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary">
            <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">Titre</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Univers</th>
              <th className="px-4 py-3">Motif du refus</th><th className="px-4 py-3">Date</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Aucun article rejeté pour l'instant.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} data-testid={`rejected-row-${c.id}`} className="hover:bg-secondary/40 transition-colors">
                <td className="px-4 py-3 font-medium max-w-md">{c.title_primary}</td>
                <td className="px-4 py-3 text-muted-foreground text-xs">{typeLabel(c.content_type)}</td>
                <td className="px-4 py-3 text-muted-foreground text-xs">{uniLabel(c.universe)}</td>
                <td className="px-4 py-3 text-muted-foreground text-xs max-w-xs">{c.reviewer_notes || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{fmt(c.updated_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`relaunch-${c.id}`} onClick={() => openSuggest(c)}
                    className="inline-flex items-center gap-1.5 text-xs border border-brand text-brand hover:bg-brand-soft px-3 py-1.5 rounded-md transition-colors">
                    <ArrowsClockwise size={13} /> Relancer un sujet amélioré
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-brand">Aperçu de l'angle amélioré</DialogTitle>
            <DialogDescription>L'IA a repensé le sujet rejeté. Ajustez le titre proposé si besoin, puis lancez la génération.</DialogDescription>
          </DialogHeader>
          {dlg && (
            <div className="space-y-4">
              <div className="text-xs text-muted-foreground">
                Sujet rejeté : <span className="italic">{dlg.oldTitle}</span>
                <div className="mt-1">Univers : <b>{uniLabel(dlg.universe)}</b> · Type : <b>{typeLabel(dlg.content_type)}</b></div>
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Angle proposé (modifiable)</label>
                {dlg.loading ? (
                  <div className="mt-2 flex items-center gap-2 text-sm text-ai"><CircleNotch size={16} className="ai-pulse" /> L'IA prépare une proposition améliorée…</div>
                ) : (
                  <textarea data-testid="relaunch-angle-input" value={dlg.title} rows={3}
                    onChange={(e) => setDlg({ ...dlg, title: e.target.value })}
                    className="mt-2 w-full px-3 py-2 rounded-md border border-border text-sm font-serif text-brand leading-snug" />
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setDlg(null)} className="px-4 py-2 rounded-md border border-border text-sm hover:bg-secondary transition-colors">Annuler</button>
                <button data-testid="confirm-relaunch-btn" onClick={generate} disabled={dlg.loading || dlg.generating || !dlg.title?.trim()}
                  className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-60">
                  {dlg.generating ? <CircleNotch size={15} className="ai-pulse" /> : <Sparkle size={15} weight="fill" />} Générer l'article
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
